import { useState } from 'react';

import type { FonteTribunal } from '@/api/fontesTribunais';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { useNotification } from '@/context/NotificationProvider';
import { useAtualizarFonteTribunal } from '@/hooks/useFontesTribunais';
import { classifyError, getUserVisibleErrorMessage, notifyMutationError } from '@/lib/errors';
import { rotuloGrau } from './fontesTribunais';

interface EditarUrlFonteDialogProps {
  fonte: FonteTribunal;
  onClose: () => void;
}

/** Montado com `key` da fonte: o campo começa com a URL dela a cada abertura. */
export function EditarUrlFonteDialog({ fonte, onClose }: EditarUrlFonteDialogProps) {
  const { showSuccess, showError } = useNotification();
  const atualizar = useAtualizarFonteTribunal();
  const [url, setUrl] = useState(fonte.url ?? '');
  const [erro, setErro] = useState<string | null>(null);

  const habilitada = fonte.consulta_nativa_habilitada || fonte.peticionamento_nativo_habilitado;
  const urlLimpa = url.trim();
  const podeSalvar = urlLimpa.length > 0 && urlLimpa !== fonte.url && !atualizar.isPending;

  const enviar = (novaUrl: string, mensagem: string) => {
    setErro(null);
    atualizar.mutate(
      { idFonte: fonte.id_fonte, input: { url: novaUrl } },
      {
        onSuccess: () => {
          showSuccess(mensagem);
          onClose();
        },
        onError: (err) => {
          // Só a URL recusada (400) é do campo; a recusa de habilitação (422) vai ao aviso geral.
          if (classifyError(err).status === 400) {
            setErro(getUserVisibleErrorMessage(err));
            return;
          }
          notifyMutationError(err, showError);
        },
      },
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (podeSalvar) enviar(urlLimpa, 'URL salva.');
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            URL do sistema — {fonte.tribunal_sigla} {rotuloGrau(fonte.instancia)}
          </DialogTitle>
          <DialogDescription>
            Endereço do sistema processual deste tribunal e grau ({fonte.sistema_processual}). Só https no domínio do
            próprio tribunal.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="fonte-url">URL</Label>
            <Input
              id="fonte-url"
              inputMode="url"
              placeholder={`https://pje1g.${(fonte.tribunal_sigla ?? 'tribunal').toLowerCase()}.jus.br/pje`}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              aria-invalid={erro !== null}
              aria-describedby={erro ? 'fonte-url-erro' : undefined}
            />
            {erro && (
              <p id="fonte-url-erro" className="text-sm text-destructive">
                {erro}
              </p>
            )}
          </div>

          <DialogFooter className="gap-2 sm:justify-between">
            {fonte.url ? (
              <Button
                type="button"
                variant="ghost"
                className="text-destructive"
                disabled={habilitada || atualizar.isPending}
                title={habilitada ? 'Desligue a conferência e o peticionamento antes.' : undefined}
                onClick={() => enviar('', 'URL removida.')}
              >
                Remover URL
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancelar
              </Button>
              <Button type="submit" disabled={!podeSalvar}>
                {atualizar.isPending ? <Spinner size={16} className="text-inherit mr-1.5" /> : null}
                Salvar
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
